import { store } from "../store";
import DataStructure, { sanitize } from "./DataStructure";
import DocumentRef from "./DocumentRef";
import COLORS from "../colors";
import { deepFreeze, timestampToDate } from "../util/misc";
import { router } from "../router";
import { DialogRouter } from "../boot/dialogs";
import { forEach, isFunction, last, zipObject } from "lodash";

//#region  Fields

export const SALE_LABOR_STATUS_FIELDS = {
  quantityReady: 0,
  quantityCompleted: 0,
  hoursScheduled: 0,
  hoursCompleted: 0,
  hoursInvoiced: 0,
  fullyScheduled: false,
  fullyCompleted: false,
  fullyInvoiced: false,
  events: {},
  loaded: {},
  installed: {},
  reports: [],
  deliveryVehicle: null,
  loadingStarted: null,
  loadingCompleted: null,
  lastDeparted: null,
  lastClockIn: null,
  lastClockOut: null,
  log: {},
  rescheduleReason: null,
  undeliveredUnloaded: false,
  signature: null,
  signerName: null,
  noSignature: false,
  invoice: null,
  pdf: null,
};
deepFreeze(SALE_LABOR_STATUS_FIELDS);

//#region  Actions

function goToLoadingTruck() {
  router.push({
    name: "loading-truck-labor",
    params: {
      projectID: this._parent.projectID,
      saleID: this._parent.saleID,
      laborID: this._parent.id,
    },
  });
}

function goToScheduling() {
  router.push({
    name: "calendar",
    params: {
      view: "scheduler",
      action: "add",
      type: "installation",
      item: this._parent.projectID + this._parent.saleID,
    },
  });
}

function viewLabor() {
  router.push({
    name: "sale-labor",
    params: {
      projectID: this._parent.projectID,
      saleID: this._parent.saleID,
      laborID: this._parent.id,
    },
  });
}

function unloadTruck() {
  const event = this._parent.todaysEvent || last(this._parent.events);
  if (!event) {
    console.warn("No event found", this._parent);
    return;
  }
  DialogRouter.push({
    name: "sale-labor-unload",
    params: {
      projectID: this._parent.projectID,
      saleID: this._parent.saleID,
      laborID: this._parent.id,
    },
  });
}
function edit() {
  const event = this._parent.nextEvent || last(this._parent.events);
  if (!event) {
    console.warn("No event found", this._parent);
    return;
  }
  DialogRouter.push({
    name: "event-edit",
    params: {
      eventID: event.id,
    },
  });
}

//#region Types

export const SALE_LABOR_STATUS_TYPES = {
  unload: {
    order: 0,
    label: "Unload",
    icon: "unloading",
    color: "negative",
    action: unloadTruck,
  },
  production: {
    order: 1,
    label: "In production",
    icon: "vendor",
    color: "positive",
  },
  reschedule: {
    order: 2,
    label: "Reschedule",
    icon: "reschedule",
    color: "negative",
    action: edit,
  },
  schedule: {
    order: 3,
    label: "Scheduling",
    icon: "scheduling",
    color: "warning",
    action: goToScheduling,
  },
  readyLoad: {
    order: 4,
    label: "Ready to load",
    icon: "loading_ready",
    color: "warning",
    action: goToLoadingTruck,
  },
  loading: {
    order: 5,
    label: "Loading onto truck",
    icon: "loading",
    color: "warning",
    time: "loadingStarted",
    action: goToLoadingTruck,
  },
  readyDepart: {
    order: 6,
    label: "Ready for transit",
    icon: "loaded",
    color: "positive",
    time(data) {
      if (data.lastClockOut && data.loadingCompleted) {
        return data.lastClockOut > data.loadingCompleted
          ? data.lastClockOut
          : data.loadingCompleted;
      } else {
        return data.lastClockOut || data.loadingCompleted;
      }
    },
    action: viewLabor,
  },
  transit: {
    order: 7,
    label: "In transit",
    icon: "delivering",
    color: "warning",
    time: "lastDeparted",
    action: viewLabor,
  },
  arrived: {
    order: 8,
    label: "In progress",
    icon: "pending",
    color: "warning",
    time: "lastClockIn",
    action: viewLabor,
  },
  complete: {
    order: 9,
    label: "Complete",
    icon: "done",
    color: "positive",
    time: "lastClockOut",
    action: viewLabor,
  },
};
deepFreeze(SALE_LABOR_STATUS_TYPES);

// #region Init

export default class SaleLaborStatus extends DataStructure {
  constructor(parent, data = {}) {
    data = sanitize(data, SALE_LABOR_STATUS_FIELDS);

    // Convert to Date
    data.loadingStarted = timestampToDate(data.loadingStarted);
    data.loadingCompleted = timestampToDate(data.loadingCompleted);
    data.lastDeparted = timestampToDate(data.lastDeparted);
    data.lastClockIn = timestampToDate(data.lastClockIn);
    data.lastClockOut = timestampToDate(data.lastClockOut);
    forEach(data.log, (event) => {
      event.timestamp = timestampToDate(event.timestamp);
    });

    super(data);

    this._parent = parent;
    if (!this.data.type) {
      this.data.type = this.get();
    }
  }

  get() {
    const loadableItems = this._parent.loadableItems;
    const itemsLoaded = this.itemsLoaded;
    // A non-third-party labor is "In production" only while it has nothing to
    // act on yet: nothing received (QC-ready), nothing loaded, nothing
    // installed. The gate previously required EVERY item to be ready
    // (`quantityReady < quantity`), which froze partially-received installs and
    // meant initiating work (scheduling/loading/installing) never advanced the
    // status. Once any material is on hand the crew can begin.
    const hasWorkableMaterial =
      this.data.quantityReady > 0 ||
      this.loadedCount > 0 ||
      this.installedCount > 0;
    if (this.data.rescheduleReason && this.quantityToUnload) {
      return "unload";
    } else if (this.data.rescheduleReason) {
      return "reschedule";
    } else if (!this._parent.data.thirdPartyItems && !hasWorkableMaterial) {
      return "production";
    } else if (!this.data.hoursScheduled) {
      return "schedule";
    } else if (this.data.fullyCompleted) {
      return "complete";
    } else if (
      this._parent.data.items.length > 0 &&
      // Only route through the loading steps when there is received-but-
      // undelivered inventory to put on the truck. When everything on hand is
      // already delivered (and the remainder simply hasn't been received yet)
      // there is nothing to load, so the labor skips straight to transit and
      // the crew departs to install what is available (sale 04100036A).
      loadableItems.length > 0 &&
      (!this.loadingStarted ||
        !this.loadingCompleted ||
        (this.loadingCompleted < this.lastClockOut &&
          // Previously loaded items
          loadableItems.filter(
            (item) =>
              item.quantity > itemsLoaded[item.id] + item.quantityDelivered
          ).length))
    ) {
      if (!this.loadingStarted || this.loadingStarted < this.loadingCompleted) {
        return "readyLoad";
      } else {
        return "loading";
      }
    } else if (!this.lastDeparted || this.lastDeparted < this.lastClockOut) {
      return "readyDepart";
    } else if (!this.lastClockIn || this.lastClockIn < this.lastDeparted) {
      return "transit";
    } else if (!this.lastClockOut || this.lastClockOut < this.lastClockIn) {
      return "arrived";
    }
  }

  //#region Formatting

  get lastReport() {
    const key = last(
      Object.keys(this.log)
        .filter((key) => this.log[key].event === "clock-out")
        .sort()
    );
    return key ? this.log[key] : null;
  }

  get deliveryVehicle() {
    return this.data.deliveryVehicle
      ? store.state.vehicles[this.data.deliveryVehicle]
      : null;
  }

  get loadedCount() {
    return Object.values(this.data.loaded).reduce(
      (total, count) => total + count,
      0
    );
  }

  get installedCount() {
    return Object.values(this.data.installed).reduce(
      (total, count) => total + count,
      0
    );
  }

  get newlyCompletedCount() {
    return this.installedCount - this.data.quantityCompleted;
  }

  get percentLoaded() {
    const numerator = this.loadedCount;
    const denominator = this._parent.data.quantity;
    const percent = (100 * numerator) / denominator;
    return { percent, numerator, denominator };
  }

  get percentInstalled() {
    const numerator = this.installedCount;
    const denominator = this._parent.data.quantity;
    const percent = (100 * numerator) / denominator;
    return { percent, numerator, denominator };
  }

  get hoursCompleted() {
    const numerator = this.data.hoursCompleted;
    const denominator = this._parent.data.manHours;
    const percent = (100 * numerator) / denominator;
    return { percent, numerator, denominator };
  }

  get itemsCompleted() {
    const numerator = Object.values(this.data.installed).reduce(
      (total, quantity) => total + quantity,
      0
    );
    const denominator = this._parent.data.quantity;
    const percent = (100 * numerator) / denominator;
    return { percent, numerator, denominator };
  }

  get isFullyInstalled() {
    return this.installedCount >= this._parent.data.quantity;
  }

  get itemsLoaded() {
    let items = {};
    forEach(this.data.loaded, (quantity, key) => {
      let id = key.split("-")[0];
      if (id in items) {
        items[id] += quantity;
      } else {
        items[id] = quantity;
      }
    });
    return items;
  }

  get itemsToUnload() {
    let items = {};
    forEach(this.data.loaded, (quantity, key) => {
      let id = key.split("-")[0];
      if (id in items) {
        items[id] += quantity;
      } else {
        items[id] = quantity;
      }
    });
    return items;
  }

  get quantityToUnload() {
    return Object.values(this.itemsToUnload).reduce(
      (total, count) => total + count,
      0
    );
  }

  get pdf() {
    return this.data.pdf ? new DocumentRef(this.data.pdf) : null;
  }

  get invoice() {
    return this.data.invoice
      ? store.state.main.invoices[this.data.invoice]
      : null;
  }

  get time() {
    const type = SALE_LABOR_STATUS_TYPES[this.get()];
    return type.time
      ? isFunction(type.time)
        ? type.time(this.data)
        : this.data[type.time]
      : null;
  }

  get type() {
    const type = SALE_LABOR_STATUS_TYPES[this.get()];
    return type ? { ...type, ...COLORS[type.color] } : null;
  }

  get action() {
    let type = this.type;
    return type && type.action ? type.action.bind(this) : null;
  }

  //#region Adjacent Labors

  get adjacentLaborItemCounts() {
    const items = this._parent.adjacentLaborItems;
    return zipObject(
      items.map((i) => i.id),
      items.map((i) => i.data.quantity)
    );
  }

  get adjacentLaborItemInstalledCounts() {
    const items = this._parent.adjacentLaborItems;
    return zipObject(
      items.map((i) => i.id),
      items.map((i) => i.data.quantityInstalled)
    );
  }

  get adjacentLaborItemRemainingCounts() {
    const items = this._parent.adjacentLaborItems.filter(
      (i) => !i.data.fullyDelivered
    );
    return zipObject(
      items.map((i) => i.id),
      items.map((i) => i.data.quantity - i.data.quantityDelivered)
    );
  }

  get lastAdjacentReport() {
    const report = {
      totalHours: 0,
      totalInstalled: 0,
      unresolvedIssues: 0,
    };
    this._parent.adjacentLabors.forEach((labor) => {
      const lastReport = labor.status.lastReport;
      if (!lastReport) {
        return;
      }
      report.totalHours += lastReport.totalHours;
      report.totalInstalled += lastReport.totalInstalled;
      report.unresolvedIssues += lastReport.unresolvedIssues;
    });
    return report;
  }

  getTotalHoursCompleted(newHours = 0) {
    let numerator = 0;
    let denominator = 0;
    this._parent.adjacentLabors.forEach((labor) => {
      numerator += labor.data.status.hoursCompleted;
      denominator += labor.data.manHours;
    });
    let percent = denominator ? (100 * numerator) / denominator : 0;

    if (this.data.type === "arrived") {
      numerator += newHours;
      percent = denominator ? (100 * numerator) / denominator : 0;
      numerator = Math.round(numerator);
    }

    return { percent, numerator, denominator };
  }

  get totalHoursCompleted() {
    return this.getTotalHoursCompleted();
  }
}
